import React, { Children, cloneElement, isValidElement, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Tabs } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import { useFormContext } from 'react-admin';
import { useFormState } from 'react-final-form';
import { useHistory, useLocation } from 'react-router-dom';
import { flattenErrorSources, tabFullPath } from './formErrors';

// A drop-in replacement for react-admin's TabbedFormTabs that makes validation
// failures on a tab the user is NOT looking at visible and reachable (NBU-9).
//
// Pass it via TabbedForm's documented `tabs` prop:
//     <TabbedForm tabs={<ErrorAwareFormTabs />} ...>
//
// It does two things react-admin's own tabs do not:
//
//  1. Marks an invalid tab with a warning icon and a field count, not colour
//     alone. React-admin already applies `errorTabButton`
//     (ra-ui-materialui form/TabbedFormView.js) to tint the label red, but that
//     is a weak signal here for three reasons: the console runs a dark theme
//     where a tinted 11px label is easy to miss; colour alone is not a
//     sufficient indicator (WCAG 1.4.1); and `.MuiTab-textColorPrimary.Mui-selected`
//     is two classes, so it outranks the single-class tint and the CURRENTLY
//     SELECTED tab cannot go red at all. The icon has none of those problems.
//     The stock tint is left in place - `classes` is still forwarded to each
//     header - so this only ever adds signal.
//
//  2. Jumps to the first offending tab when a save is blocked, so the user
//     lands on the field instead of hunting for it.

const useStyles = makeStyles(theme => ({
  label: {
    display: 'inline-flex',
    alignItems: 'center',
    // A plain flex container, so the CSS `gap` property applies directly.
    gap: theme.spacing(0.75)
  },
  // No background is pinned here, so the theme's own foreground rules still
  // apply to the label text - only the icon and count take an explicit colour.
  icon: { fontSize: '1rem', color: theme.palette.error.main },
  count: { color: theme.palette.error.main, fontWeight: 600, fontSize: '0.75rem' }
}));

/**
 * How many of a tab's fields are currently reporting a validation error.
 *
 * `getGroupFields` is the registry react-admin already maintains: every input
 * rendered through useInput registers its source against the enclosing
 * FormGroup, and FormTab opens one group per tab named after the tab's route
 * (ra-core form/useInput.js, ra-ui-materialui form/FormTab.js). So the mapping
 * from field to tab is free and cannot drift from the rendered form - there is
 * no hand-maintained list of which field lives where.
 *
 * Exported for testing.
 *
 * @param {object} formContext react-admin's form context
 * @param {string} groupName the tab's full route
 * @param {string[]} errorSources flattened sources currently in error
 * @param {object} touched final-form's touched map
 * @param {boolean} submitFailed
 * @returns {number}
 */
export function tabErrorCount (formContext, groupName, errorSources, touched, submitFailed) {
  if (!formContext || typeof formContext.getGroupFields !== 'function') return 0;
  if (errorSources.length === 0) return 0;
  const fields = formContext.getGroupFields(groupName) || [];
  return fields.filter(field =>
    errorSources.includes(field) &&
    // The same condition react-admin uses for its own error tint: a field the
    // user has never touched, on a form never submitted, is not yet a mistake.
    (submitFailed || (touched && touched[field]))
  ).length;
}

/**
 * A tab's label, annotated with its invalid-field count.
 *
 * Takes the count as a prop rather than deriving it: the parent already has the
 * whole form state, and computing it there keeps the counts and the jump target
 * from being derived two different ways.
 */
export const FormTabErrorLabel = ({ label, count }) => {
  const classes = useStyles();
  // `label || null`, never a bare `label`: a component that returns undefined
  // is a React error ("Nothing was returned from render"), which took down the
  // whole campaign screen the first time a tab turned out not to carry one.
  // ErrorAwareFormTabs no longer mounts this without a label, but the guard
  // stays - a missing label must degrade to an unlabelled tab, not a crash.
  if (!count) return label || null;
  const plural = count === 1 ? '' : 's';
  return (
    <span className={classes.label}>
      {label}
      <ErrorOutlineIcon
        className={classes.icon}
        titleAccess={`${count} field${plural} on this tab need${count === 1 ? 's' : ''} attention`} />
      <span className={classes.count}>{count}</span>
    </span>
  );
};

FormTabErrorLabel.propTypes = {
  label: PropTypes.node,
  count: PropTypes.number
};

/**
 * The `label` override to clone onto a tab, or `{}` to leave its label alone.
 *
 * Badging means replacing `label` with an element that wraps the original text,
 * so the original has to be READABLE from the element - and it is only readable
 * when the tab is a literal <FormTab label="x">. A tab written as a wrapper
 * component that renders its own FormTab has no `label` of its own; hoisting one
 * out of it is impossible, and injecting an element built around `undefined` is
 * what crashed the campaign create screen. Such a tab is left exactly as
 * react-admin would render it - stock label, stock error tint, no badge - so a
 * caller that forgets to pass a label loses a feature, not the whole form.
 *
 * Exported for testing.
 *
 * @param {ReactElement} tab
 * @param {number} count
 * @returns {object} props to spread onto the cloned tab
 */
export function tabLabelProps (tab, count) {
  const label = tab && tab.props ? tab.props.label : undefined;
  if (label == null) return {};
  return { label: <FormTabErrorLabel label={label} count={count} /> };
}

export const ErrorAwareFormTabs = (props) => {
  const { children, classes, url, syncWithLocation, value, onChange, ...rest } = props;
  const location = useLocation();
  const history = useHistory();
  const formContext = useFormContext();
  const { errors, touched, submitFailed } = useFormState({
    subscription: { errors: true, touched: true, submitFailed: true }
  });

  const errorSources = flattenErrorSources(errors);

  // Indices must be the RAW child indices, including any falsy children, because
  // TabbedFormView derives each tab's CONTENT route from the same raw index
  // (its Children.map counts null children too). Filtering first with
  // Children.toArray would renumber the tabs after a conditionally rendered
  // `{isEdit && <FormTab/>}` - a pattern already used elsewhere in this console
  // - and every header after it would then link to a route with no content.
  const tabs = [];
  Children.forEach(children, (tab, index) => {
    if (!isValidElement(tab)) return;
    const path = tabFullPath(tab, index, url);
    tabs.push({
      tab,
      index,
      path,
      count: tabErrorCount(formContext, path, errorSources, touched, submitFailed)
    });
  });

  // Mirrors react-admin's own guard: if the location points at a tab that is
  // not rendered yet (a permission-gated tab on first render), fall back to the
  // first tab so Material-UI does not warn about an unknown Tabs value.
  const paths = tabs.map(entry => entry.path);
  const activeValue = syncWithLocation
    ? (paths.includes(location.pathname) ? location.pathname : paths[0])
    : value;

  // Jump on the rising edge of submitFailed ONLY. The alternative - jumping
  // whenever an off-tab error exists - would yank the user away from a clean
  // tab they had deliberately navigated to, and would fire mid-keystroke as
  // live validation re-runs. submitFailed stays true once set, so a second
  // blocked save does not re-trigger this; by then the badges are already up.
  //
  // The counts read here are computed during the same render that set
  // submitFailed, so there is no window in which this fires against stale ones.
  const previouslyFailed = useRef(submitFailed);
  const firstErrorTab = tabs.find(entry => entry.count > 0);
  const activeTab = tabs.find(entry => entry.path === activeValue);
  useEffect(() => {
    const justFailed = submitFailed && !previouslyFailed.current;
    previouslyFailed.current = submitFailed;
    if (!justFailed || !firstErrorTab) return;
    // Already looking at an offending tab - leave the user where they are.
    if (activeTab && activeTab.count > 0) return;
    if (syncWithLocation) history.push(firstErrorTab.path);
    else if (onChange) onChange(null, firstErrorTab.index);
  }, [submitFailed, firstErrorTab, activeTab, syncWithLocation, history, onChange]);

  return (
    <Tabs value={activeValue} indicatorColor="primary" onChange={onChange} {...rest}>
      {tabs.map(({ tab, path, index, count }) => cloneElement(tab, {
        key: path,
        intent: 'header',
        value: syncWithLocation ? path : index,
        classes,
        syncWithLocation,
        // FormTabHeader renders a React element label as-is and only translates
        // a string one. The console registers no i18nProvider messages of its
        // own, so tab labels are already the literal English shown on screen and
        // nothing is lost by wrapping them.
        ...tabLabelProps(tab, count)
      }))}
    </Tabs>
  );
};

ErrorAwareFormTabs.propTypes = {
  children: PropTypes.node,
  classes: PropTypes.object,
  url: PropTypes.string,
  syncWithLocation: PropTypes.bool,
  value: PropTypes.any,
  onChange: PropTypes.func
};

export default ErrorAwareFormTabs;
